import { useEffect, useId } from 'react';

/**
 * Browser tab title and favicon.
 *
 * Title: "(unread) Page · Section · App". Several components can claim a title at once (the layout names the
 * section, the page header names the record); the highest priority wins, then the most recently set.
 * Favicon: the workspace's uploaded icon, else the brand mark (company initial on the primary color) with a red dot
 * while the inbox has unread mail (an uploaded icon cannot carry the dot; the count stays in the title).
 */

const DEFAULT_APP_NAME = 'Global CRM';
const BRAND_COLOR = '#2F5BEA';
const BADGE_COLOR = '#EF4444';

interface Claim {
  title: string;
  priority: number;
  seq: number;
}

const claims = new Map<string, Claim>();
let seq = 0;
let appName = DEFAULT_APP_NAME;
let unread = 0;
let customIcon: string | null = null;
let lastIcon = '';

function render() {
  let top: Claim | undefined;
  for (const c of claims.values()) if (!top || c.priority > top.priority || (c.priority === top.priority && c.seq > top.seq)) top = c;
  const parts = top && top.title !== appName ? [top.title, appName] : [appName];
  document.title = `${unread > 0 ? `(${unread > 99 ? '99+' : unread}) ` : ''}${parts.join(' · ')}`;

  const icon = customIcon ?? faviconUrl(appName, unread > 0);
  if (icon === lastIcon) return;
  lastIcon = icon;
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  if (customIcon) link.removeAttribute('type');
  else link.type = 'image/svg+xml';
  link.href = icon;
}

function faviconUrl(name: string, badge: boolean) {
  const letter = escapeXml((name.trim()[0] ?? 'G').toUpperCase());
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">` +
    `<rect width="64" height="64" rx="16" fill="${BRAND_COLOR}"/>` +
    `<text x="32" y="45" text-anchor="middle" font-family="Inter,system-ui,-apple-system,Segoe UI,Arial,sans-serif" font-size="38" font-weight="700" fill="#fff">${letter}</text>` +
    (badge ? `<circle cx="52" cy="12" r="11" fill="${BADGE_COLOR}" stroke="#fff" stroke-width="3"/>` : '') +
    `</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function escapeXml(s: string) {
  return s.replace(/[<>&"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/** Claims the tab title while the calling component is mounted (higher priority wins; pages use 1, the layout 0). */
export function useDocumentTitle(title: string | null | undefined, priority = 1) {
  const id = useId();
  useEffect(() => {
    if (!title) return;
    claims.set(id, { title, priority, seq: ++seq });
    render();
    return () => {
      claims.delete(id);
      render();
    };
  }, [id, title, priority]);
}

/** Workspace name and unread inbox count shown in every title and in the favicon; `icon` replaces the generated favicon. */
export function useDocumentBranding(name: string | null | undefined, unreadCount: number, icon?: string | null) {
  useEffect(() => {
    appName = name?.trim() || DEFAULT_APP_NAME;
    unread = unreadCount;
    customIcon = icon || null;
    render();
  }, [name, unreadCount, icon]);
  // signed-out screens go back to the plain app name and icon
  useEffect(
    () => () => {
      appName = DEFAULT_APP_NAME;
      unread = 0;
      customIcon = null;
      render();
    },
    [],
  );
}
